import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../layout/dashboardLayout";
import { getUserWalletData } from "../actions/userlogin";
import { claimDailyGoldCoins, getDailyGoldCoinStatus } from "../actions/dailyGoldCoins";
import DataFetchStatus from "../common/DataFetchStatus";
import CoinSwitcher from "../common-components/CoinSwitcher";
import PlaythroughProgress from "../common-components/PlaythroughProgress";
import { getGoldCoinName, getSweepsCoinName } from "../../utility/complianceCopy";
import { formatCoins, totalDreamCash } from "../../utility/coinFormat";

export default function DtsWallet() {
  const [wallet, setWallet] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dailyReward, setDailyReward] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimMessage, setClaimMessage] = useState("");

  const refreshWallet = async () => {
    try {
      const response = await getUserWalletData();
      if (response.status) {
        setWallet(response.result || {});
        setError("");
      } else {
        setError(response.message || "Unable to load wallet.");
      }
    } catch (err) {
      setError("Unable to load wallet.");
    } finally {
      setLoading(false);
    }
  };

  const refreshDailyReward = async () => {
    setDailyLoading(true);
    setDailyError("");
    try {
      const response = await getDailyGoldCoinStatus();
      if (response.status && response.result) {
        setDailyReward(response.result);
      } else {
        setDailyError(response.message || "Unable to load daily reward status.");
      }
    } catch (err) {
      setDailyError("Unable to load daily reward status.");
    } finally {
      setDailyLoading(false);
    }
  };

  useEffect(() => {
    refreshWallet();
    refreshDailyReward();
  }, []);

  const handleDailyClaim = async () => {
    if (claiming || !dailyReward?.enabled || dailyReward?.claimed) return;
    setClaiming(true);
    setClaimMessage("");
    setDailyError("");
    try {
      const response = await claimDailyGoldCoins();
      if (!response.status) {
        if (response.code === "ALREADY_CLAIMED") {
          await refreshDailyReward();
          setClaimMessage("Your daily Gold Coin reward has already been claimed.");
          return;
        }
        if (response.code === "DAILY_REWARD_UNAVAILABLE") {
          await refreshDailyReward();
        }
        setDailyError(response.message || "Unable to claim daily Gold Coins.");
        return;
      }
      setClaimMessage(response.message || "Daily Gold Coins added to your wallet.");
      await Promise.all([refreshWallet(), refreshDailyReward()]);
    } catch (err) {
      setDailyError("Unable to claim daily Gold Coins. Please retry.");
    } finally {
      setClaiming(false);
    }
  };

  return <DashboardLayout><main className="container dts-wallet-page">
    <h1>Wallet</h1><DataFetchStatus loading={loading} error={error}>
      <CoinSwitcher wallet={wallet} />
      <div className="dts-balance-grid">
        <article><span>{getGoldCoinName()}</span><strong>{formatCoins(wallet.gc_balance)}</strong><small>For entertainment play; no redemption value.</small></article>
        <article><span>Total {getSweepsCoinName()}</span><strong>{formatCoins(totalDreamCash(wallet))}</strong><small>Across all promotional states.</small></article>
        <article><span>Unplayed {getSweepsCoinName()}</span><strong>{formatCoins(wallet.unplayed_dc)}</strong><small>Available to play.</small></article>
        <article><span>Promotional {getSweepsCoinName()}</span><strong>{formatCoins(wallet.freebet_dc)}</strong><small>Promotional entries only.</small></article>
        <article><span>Locked {getSweepsCoinName()}</span><strong>{formatCoins(wallet.sticky_dc)}</strong><small>Unlocks after play-through.</small></article>
        <article><span>Redeemable {getSweepsCoinName()}</span><strong>{formatCoins(wallet.redeemable_dc)}</strong><small>Eligible for a prize request.</small></article>
      </div>
      <section className="dts-daily-claim" aria-labelledby="dts-daily-claim-title">
        <div className="dts-daily-claim__copy">
          <span className="dts-daily-claim__eyebrow">Daily reward</span>
          <h2 id="dts-daily-claim-title">Claim free {getGoldCoinName()}</h2>
          <p>Gold Coins are for entertainment play only and have no redemption value.</p>
        </div>
        <div className="dts-daily-claim__action">
          {dailyLoading ? <span className="dts-daily-claim__muted" role="status">Loading daily reward…</span> : null}
          {!dailyLoading && dailyError ? (
            <div className="dts-daily-claim__feedback dts-daily-claim__feedback--error" role="alert">
              <span>{dailyError}</span>
              <button type="button" className="dts-daily-claim__retry" onClick={refreshDailyReward}>Retry</button>
            </div>
          ) : null}
          {!dailyLoading && !dailyError && dailyReward?.enabled && !dailyReward?.claimed ? (
            <>
              <strong>{formatCoins(dailyReward.amount)} {getGoldCoinName()} available today</strong>
              <button
                type="button"
                className="btn img_btn dts-daily-claim__button"
                aria-label="Claim daily reward"
                onClick={handleDailyClaim}
                disabled={claiming}
              >
                {claiming ? "Adding Gold Coins…" : "Claim daily reward"}
              </button>
              <small>One claim per UTC calendar day.</small>
            </>
          ) : null}
          {!dailyLoading && !dailyError && dailyReward?.enabled && dailyReward?.claimed ? (
            <div className="dts-daily-claim__feedback" role="status">
              <strong>Already claimed today</strong>
              <span>Your next daily claim is available at 00:00 UTC.</span>
            </div>
          ) : null}
          {!dailyLoading && !dailyError && dailyReward && !dailyReward.enabled ? (
            <span className="dts-daily-claim__muted">The daily Gold Coin reward is not available right now.</span>
          ) : null}
          {claimMessage ? <span className="dts-daily-claim__success" role="status">{claimMessage}</span> : null}
        </div>
      </section>
      <PlaythroughProgress played={wallet.current_volume_played} target={wallet.target_volume} locked={wallet.sticky_dc} />
      <div className="dts-wallet-actions"><Link className="btn img_btn" to="/redeem">Prize request</Link></div>
    </DataFetchStatus>
  </main></DashboardLayout>;
}
